import { useState, useCallback, useEffect } from 'react';
import { motion, useDragControls } from 'framer-motion';
import {
  getDailyAffirmation,
  getRandomAffirmation,
  affirmationThemes,
  SubconsciousAffirmation,
} from '@/constants/subconsciousAffirmations';
import { useIsMobile } from '@/hooks/useIsMobile';
import styles from './SubconsciousMindQuote.module.css';

interface SubconsciousMindQuoteProps {
  date?: Date;
}

interface Position {
  x: number;
  y: number;
}

const STORAGE_KEY = 'subconscious-quote-position';
const MINIMIZED_KEY = 'subconscious-quote-minimized';

const DEFAULT_POSITION: Position = { x: 20, y: 100 };

// Load position from localStorage
function loadPosition(): Position {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      if (
        typeof parsed.x === 'number' &&
        typeof parsed.y === 'number' &&
        parsed.x >= 0 &&
        parsed.y >= 0
      ) {
        return parsed;
      }
    }
  } catch {
    // Ignore errors, use default
  }
  return DEFAULT_POSITION;
}

// Load minimized state from localStorage (default to minimized on mobile)
function loadMinimized(isMobileViewport: boolean): boolean {
  // On mobile, always start minimized
  if (isMobileViewport) return true;
  try {
    return localStorage.getItem(MINIMIZED_KEY) === 'true';
  } catch {
    return false;
  }
}

export function SubconsciousMindQuote({ date = new Date() }: SubconsciousMindQuoteProps) {
  const isMobile = useIsMobile();
  const [position, setPosition] = useState<Position>(DEFAULT_POSITION);
  const [isMinimized, setIsMinimized] = useState(() => loadMinimized(window.innerWidth < 768));
  const [isHovered, setIsHovered] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [currentAffirmation, setCurrentAffirmation] = useState<SubconsciousAffirmation>(() =>
    getDailyAffirmation(date)
  );
  const [isCustom, setIsCustom] = useState(false);

  const dragControls = useDragControls();

  // Load position on mount
  useEffect(() => {
    setPosition(loadPosition());
    // Don't override minimized state on mount - it's already set in useState
  }, []);

  // Update affirmation when date changes
  useEffect(() => {
    if (!isCustom) {
      setCurrentAffirmation(getDailyAffirmation(date));
    }
  }, [date, isCustom]);

  // Save position when it changes
  const handleDragEnd = useCallback((_: any, info: { point: Position }) => {
    setIsDragging(false);
    const newPos = {
      x: Math.max(0, info.point.x - 140),
      y: Math.max(0, info.point.y - 20),
    };
    setPosition(newPos);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(newPos));
    } catch {
      // Ignore storage errors
    }
  }, []);

  // Toggle minimized state
  const toggleMinimized = useCallback(() => {
    setIsMinimized((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(MINIMIZED_KEY, String(next));
      } catch {
        // Ignore
      }
      return next;
    });
  }, []);

  // Get random affirmation
  const handleRefresh = useCallback(() => {
    setCurrentAffirmation(getRandomAffirmation());
    setIsCustom(true);
  }, []);

  // Reset to daily affirmation
  const handleReset = useCallback(() => {
    setCurrentAffirmation(getDailyAffirmation(date));
    setIsCustom(false);
  }, [date]);

  const theme = affirmationThemes[currentAffirmation.theme];

  return (
    <>
      {/* Invisible drag constraints */}
      <div className={styles.constraints} />

      <motion.div
        className={`${styles.floatingContainer} ${isMinimized ? styles.minimized : ''} ${isDragging ? styles.dragging : ''}`}
        style={{
          x: position.x,
          y: position.y,
        }}
        drag
        dragControls={dragControls}
        dragMomentum={false}
        dragElastic={0.1}
        onDragStart={() => setIsDragging(true)}
        onDragEnd={handleDragEnd}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        animate={{
          opacity: isHovered || isDragging ? 1 : 0.9,
        }}
        transition={{ duration: 0.2 }}
      >
        {isMinimized ? (
          // Minimized state - just an icon button
          <motion.button
            className={styles.minimizedButton}
            onClick={toggleMinimized}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
            aria-label="Expand affirmation"
            style={{ borderColor: theme.color }}
          >
            🧠
          </motion.button>
        ) : (
          // Expanded widget
          <div className={styles.widget}>
            {/* Drag handle / Header */}
            <div
              className={styles.dragHandle}
              onPointerDown={(e) => dragControls.start(e)}
              style={{ borderBottomColor: `${theme.color}30` }}
            >
              <span className={styles.dragIcon}>⋮⋮</span>
              <span className={styles.label}>AFFIRMATION</span>
              <span
                className={styles.themeTag}
                style={{ backgroundColor: `${theme.color}20`, color: theme.color }}
              >
                {theme.emoji} {theme.name}
              </span>
              <button
                className={styles.minimizeButton}
                onClick={toggleMinimized}
                aria-label="Minimize"
              >
                −
              </button>
            </div>

            {/* Content */}
            <div className={styles.content}>
              <blockquote className={styles.quote}>
                <p className={styles.quoteText}>"{currentAffirmation.text}"</p>
                <footer className={styles.quoteFooter}>
                  <cite className={styles.author}>— Joseph Murphy</cite>
                  {currentAffirmation.chapter && (
                    <span className={styles.source}>{currentAffirmation.chapter}</span>
                  )}
                </footer>
              </blockquote>
            </div>

            {/* Footer */}
            <div className={styles.footer}>
              <span className={styles.bookTitle}>The Power of Your Subconscious Mind</span>
              <div className={styles.actions}>
                {isCustom && (
                  <button
                    className={styles.actionButton}
                    onClick={handleReset}
                    aria-label="Return to daily affirmation"
                    title="Return to daily"
                  >
                    ◉
                  </button>
                )}
                <button
                  className={styles.actionButton}
                  onClick={handleRefresh}
                  aria-label="Get random affirmation"
                  title="Random affirmation"
                >
                  ↻
                </button>
              </div>
            </div>
          </div>
        )}
      </motion.div>
    </>
  );
}
